import React, { FC, memo, useCallback, useEffect } from 'react';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import styled from '@emotion/styled';
import { useState } from 'react';
import { CartDiscount } from '~Components/CartProvider';
import { useStorage } from '~Components/Storage';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import { useBongLayout } from '~Providers/BongLayout';
import { useButton, useCurrency, useDeviceInfo, useI18n, useLicenses, useSelector } from '~Hooks';
import { useEntryFinder } from '~Components/EntryFinder';
import { CartItem } from '~Types';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { SERVICE_TYPE } from '../../../../../__queries__';
import OrderIdManager from '~Components/OrderIdManager';
import { Dimmer } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import PayButtons from '~Scenes/Tables/Components/PayButtons';
import { sortByCourseTypeOrder } from '~Utils/sortArticles';
import { useApolloClient } from '@apollo/client';
import { combineArticles } from '~Utils/combineArticles';
import { useStaffContext } from '~Scenes/Handy/Context/StaffProvider';
import { useCompetition } from '~Providers/CompetitionsProvider';
import CartHeader from '../Ordering/Cart/CartHeader';
import DiscountButton from '~Components/DiscountButton';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';

const Container = styled.div`
  position: absolute;
  box-sizing: border-box;
  width: 100%;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  padding-right: 8px;
  z-index: 3;
`;

const CartElement = styled.div`
  background: var(--colorBlueGray1300);
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2), 0 0 12px rgba(0, 0, 0, 0.1);
  width: calc(100vw - 97px);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  z-index: 2;
`;

const Content = styled.div<{ isPhone?: boolean }>`
  overflow-y: scroll;
  transition: all 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
  padding-bottom: 52px;
  max-height: ${({ isPhone }) => (isPhone ? 'calc(100vh - 400px)' : 'calc(100vh - 320px)')};
  height: auto;
  min-height: 82px;
`;

const PayButtonsWrapper = styled.div<{ isPhone: boolean }>`
  padding-top: 4px;
  padding-bottom: ${({ isPhone }) => (isPhone ? '24px' : '0')};
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: -30px;
    left: 0;
    height: 30px;
    background: linear-gradient(0deg, var(--colorBlueGray1300), transparent);
    width: 100%;
  }
`;

const Buttons = styled.div`
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemWrapper = styled.li`
  list-style: none;
  display: flex;
  position: relative;
  border-bottom: 1px solid #1c232c;
  padding: 10px 16px 10px 12px;
  align-items: center;

  p {
    padding-right: 12px;
    margin: 0;
    word-break: break-word;
  }
`;

const ItemPrice = styled.span`
  padding-left: 12px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InitialPrice = styled.span`
  color: var(--colorBlueGray600);
  text-decoration: line-through;
  font-size: 12px;
`;

const Qty = styled.span`
  width: 32px;
  margin-right: 8px;
  display: inline-block;
  min-width: 32px;
`;

interface Props {
  serviceType: SERVICE_TYPE;
  clearCart: () => void;
  setAppliedDiscountForCart: (value: React.SetStateAction<CartDiscount | null>) => void;
  /** When true, automatically initiates payment as soon as the cheque is ready */
  initiatePaymentOnOpen?: boolean;
  /** Called when auto-payment has been initiated, so the parent can reset the flag */
  onAutoPaymentInitiated?: () => void;
}

const QSRCheque: FC<Props> = ({
  serviceType,
  clearCart,
  setAppliedDiscountForCart,
  initiatePaymentOnOpen,
  onAutoPaymentInitiated,
}) => {
  const findEntryFromItem = useEntryFinder();
  const { selectedCheque, setSelectedCheque } = useChequeContext();
  const universe = useTableUniverse();
  const tableApi = useTableApi({
    private: true,
  });
  const venueId = useSelector((state) => state.venueId);
  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);
  const [easyId, setEasyId] = useState('');
  const deviceInfo = useDeviceInfo();
  const currency = useCurrency();
  const { i18n } = useI18n();
  const client = useApolloClient();
  const { authedStaff } = useStaffContext();
  const competitions = useCompetition();
  const [storage] = useStorage();
  const layout = useBongLayout();
  const isPhone = deviceInfo?.isPhone;
  const licenses = useLicenses();
  const [ongoingOrder, setOngoingOrder] = useState(false);
  const { selectedTable } = useTableContext();

  // We use client ID to create cheque and table id's. Makes them unique across devices/clients
  // while the identifyer is still easily accesible through code on the same device.
  const [{ clientId }] = useStorage();
  const cheques = universe.cheques.filter((c) => c.tableId === `POS_${clientId}`);
  const cheque = cheques[0];
  const chequeItems = universe.items.filter((i) => i.chequeId === cheque?.id);
  const messages = universe.messages.filter((m) => m.chequeId === cheque?.id);

  const order = useSelector(
    (state) => state.POSOrders.filter((o) => o.chequeId === selectedCheque?.id)[0] || null,
  );

  const payment = useSelector(
    (state) => state.POSPayments.filter((p) => order && p.order === order.id)[0] || null,
  );

  useEffect(() => {
    if (!cheque) {
      return;
    }

    setSelectedCheque(cheque);
  }, [cheque]);

  useEffect(() => {
    if (payment?.status === 'PENDING') {
      setOngoingOrder(true);
    }

    // If the payment is completed, clear the cart even if it is a partial payment.
    // If there is a partial payment the cheque will be "locked" anyways.
    if (payment?.status === 'COMPLETED') {
      clearCart();

      // Reset to the first category unless the user has opted to stay on the current one
      if (!storage.stayOnCategoryAfterPayment) {
        const firstCategoryId = layout.categories[0]?.id;
        if (firstCategoryId) {
          layout.setSelectedCategoryId(firstCategoryId);
        }
      }
    }
  }, [payment]);

  useEffect(() => {
    // If there are no payments and a payment has been in progress, send the order to the kitchen
    if (!payment && ongoingOrder && !cheque) {
      setSelectedCheque(null);
      handleSendToKitchen(easyId);
      setOngoingOrder(false);
      setAppliedDiscountForCart(null);
    }
  }, [payment, ongoingOrder, cheque]);

  const removeCheque = useCallback(() => {
    // Cheque will never be paid here but just for safety
    if (!cheque || (cheque && cheque?.paidAmount > 0)) {
      return;
    }

    setAppliedDiscountForCart(null);
    tableApi.removeCheque(cheque?.id, cheque?.tableId || '');
    setSelectedCheque(null);
  }, [cheque?.paidAmount, cheque?.id, tableApi.removeCheque, setSelectedCheque]);

  const dimmerClickHandlers = useButton({
    onClick: () => {
      removeCheque();
    },
  });

  // Items are removed from the table when the cheque is paid, so we need
  // to store the items to be able to send them to the kitchen.
  const [itemsToSendToKitchen, setItemsToSendToKitchen] = useState<CartItem[]>([]);
  const [messagesToSendToKitchen, setMessagesToSendToKitchen] = useState<string[]>([]);
  useEffect(() => {
    /**
     * This is slightly confusing, but here's the explanation:
     *
     * If a payment has started, store the items and use them to send to the kitchen.
     *
     * If a payment has not started, do nothing. The items will stay in the array after the
     * payment is over but it's okay because we are not referencing them until after
     * a payment is completed. And the array will be refreshed when the new payment starts.
     */
    if (!ongoingOrder) {
      return;
    }

    const mappedItems: CartItem[] = chequeItems.reduce((arr, item) => {
      const entry = findEntryFromItem(item.itemId);

      if (!entry) {
        return arr;
      }

      arr.push({
        ...item,
        count: 1,
        entryId: entry.id,
        description: '',
        removedVariations: item.removedVariations,
        discountTitle: item.discountTitle || undefined,
        variations:
          item.richVariations.length > 0
            ? item.richVariations
            : item.variations.map((v) => ({
                //Legacy? not sure if needed anymore
                id: v,
                name: v,
                price: 0,
              })),
        isMessage: item.title.includes('[m]'),
      });

      return arr;
    }, [] as CartItem[]);

    setItemsToSendToKitchen(mappedItems);

    const formattedMessages = messages?.map((m) => m.message) ?? [];

    setMessagesToSendToKitchen(formattedMessages);
  }, [ongoingOrder]);

  /**
   * When payment is completed, send the order to the kitchen
   */
  function handleSendToKitchen(id: string) {
    const items = itemsToSendToKitchen.filter((item) => !item.isMessage);

    tableApi.sendKitchenBong(
      messagesToSendToKitchen,
      items,
      id,
      undefined,
      undefined,
      undefined,
      serviceType,
    );

    if (authedStaff?.id) {
      competitions.addSale({
        staffId: authedStaff.id,
        items: itemsToSendToKitchen,
      });
    }

    // Reduce inventory for each item (if the item has an inventory count)
    itemsToSendToKitchen.forEach((item) => {
      const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

      if (invItem && typeof invItem.count === 'number') {
        inventoryApi.reduceStock(invItem.itemId, item.fraction, 'Order');
      }
    });
  }

  if (!venueId) {
    return null;
  }

  if (!cheque) {
    return null;
  }

  const sortedItems = chequeItems?.length > 0 ? sortByCourseTypeOrder(chequeItems, client) : [];

  const itemsArr = storage.compressedCart ? combineArticles(sortedItems) : sortedItems;

  return (
    <div>
      <Dimmer {...dimmerClickHandlers} amount="low" active={Boolean(cheque)} />
      <Container>
        <CartElement data-testid="cart">
          <CartHeader
            title={chequeItems?.length + ' ' + i18n('Handy.NrOfItems', chequeItems?.length)}
            total={cheque.amount}
            discount={cheque.discount}
            cartExpanded={true}
            onCartExpanded={removeCheque}
          />
          <Content isPhone={Boolean(isPhone)}>
            <div>
              {itemsArr.map((item) => {
                const itemCount = ('count' in item ? item.count : item.fraction) ?? 1;

                return (
                  <ItemWrapper key={item.id}>
                    <Qty>{Math.round(itemCount * 100) / 100}</Qty>
                    <p>
                      {item.title}
                      {item.message && (
                        <i>
                          <br />
                          {item.message}
                        </i>
                      )}
                      {item.richVariations.map((variation) => (
                        <Paragraph
                          key={variation.id}
                          level={4}
                          color="var(--colorBlueGray600)"
                          bottom={4}>
                          {variation.price > 0
                            ? `${variation.name} (${currency.format(variation.price)})`
                            : variation.name}
                        </Paragraph>
                      ))}
                      {item.removedVariations.map((variation) => (
                        <Paragraph
                          key={variation.id}
                          level={4}
                          color="var(--colorUIRed)"
                          bottom={4}>
                          -{variation.name}
                        </Paragraph>
                      ))}
                    </p>
                    <ItemPrice>
                      {item.discount > 0 && (
                        <InitialPrice>{currency.format(item.amount)}</InitialPrice>
                      )}
                      {currency.format(item.amount - item.discount)}
                    </ItemPrice>
                  </ItemWrapper>
                );
              })}

              {cheque.serviceCharge && (
                <ItemWrapper>
                  <Qty>1</Qty>
                  <p>
                    {i18n('Handy.Cheque.Service')} {cheque.serviceCharge.percentage}%
                  </p>
                  <ItemPrice>{currency.format(cheque.serviceCharge.amount)}</ItemPrice>
                </ItemWrapper>
              )}
            </div>
          </Content>
          <PayButtonsWrapper isPhone={Boolean(isPhone)}>
            <Buttons>
              {(licenses.hasHandheldPro || licenses.hasHandheldOrder) && (
                // TODO: Make selecting items possible and pass selected item IDs to the DiscountButton
                <DiscountButton selectedItemIds={[]} selectedChequeId={cheque.id} />
              )}
              <PayButtons
                easyId={easyId}
                ongoingOrder={ongoingOrder}
                serviceType={serviceType}
                cancelOrder={() => setOngoingOrder(false)}
                selectedTable={selectedTable}
                selectedChequeId={cheque.id}
                isQSR={true}
                autoInitiatePayment={initiatePaymentOnOpen && easyId !== ''}
                onAutoPaymentInitiated={onAutoPaymentInitiated}
                onEasyIdChange={setEasyId}
              />
            </Buttons>
          </PayButtonsWrapper>
        </CartElement>
      </Container>

      {venueId && (
        <OrderIdManager
          venueId={venueId}
          setOrderEasyId={setEasyId}
          orderId={order?.id}
          reset={() => {}}
          visible={false}
        />
      )}
    </div>
  );
};

// Only change on serviceType and initatePaymentOnOpen
export default memo(QSRCheque, (prevProps, nextProps) => {
  return (
    prevProps.serviceType === nextProps.serviceType &&
    prevProps.initiatePaymentOnOpen === nextProps.initiatePaymentOnOpen &&
    prevProps.onAutoPaymentInitiated === nextProps.onAutoPaymentInitiated
  );
});
